import { t } from '../../../../i18n/i18n';

/**
 * Sign-ups by country as a ring — live draws this with an echarts pie; the
 * charts here are inline SVG, so this is the same ring without the library.
 *
 * Every colour is an existing token's raw value (grad-tabs blue, chip-info,
 * capital-up, warning-text, the histogram indigo, chip-danger, on-accent,
 * text-soft) — a categorical chart needs more than the up/down pair, but not
 * new colours.
 */
const PALETTE = ['#1a5dfd', '#6ea8ff', '#22c55e', '#f7d774', '#7b8dff', '#fd6b74', '#c5cffd', '#9a9a9a'];

const SIZE = 168;
const R = 64;
const STROKE = 18;
const C = 2 * Math.PI * R;
/** A hair of canvas between neighbouring slices, so equal colours can't merge. */
const GAP = 2;

export default function CountryDonut({ countries, selected, onSelect }) {
  const total = countries.reduce((sum, c) => sum + c.joined, 0);
  const many = countries.filter((c) => c.joined > 0).length > 1;

  let offset = 0;
  const slices = countries.map((c, i) => {
    const length = total ? (c.joined / total) * C : 0;
    const slice = {
      ...c,
      color: PALETTE[i % PALETTE.length],
      dash: Math.max(length - (many ? GAP : 0), 0),
      offset,
    };
    offset += length;
    return slice;
  });

  const share = (joined) => (total ? `${((joined / total) * 100).toFixed(1)}%` : '0%');

  return (
    <div className="country-donut">
      <svg
        className="country-donut__ring"
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={t('Sign-ups by country, {{total}} in total', { total })}
      >
        <circle className="country-donut__track" cx={SIZE / 2} cy={SIZE / 2} r={R} strokeWidth={STROKE} fill="none" />
        {slices.map((s) =>
          s.dash > 0 ? (
            <circle
              key={s.name}
              className={`country-donut__slice${s.name === selected ? ' is-on' : ''}`}
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={R}
              fill="none"
              stroke={s.color}
              strokeWidth={STROKE}
              strokeDasharray={`${s.dash} ${C - s.dash}`}
              strokeDashoffset={-s.offset}
              transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
              onMouseEnter={() => onSelect(s.name)}
            >
              <title>{t('{{country}}: {{count}} joined ({{share}})', { country: s.label, count: s.joined, share: share(s.joined) })}</title>
            </circle>
          ) : null
        )}
        <text className="country-donut__total" x="50%" y="48%" textAnchor="middle" dominantBaseline="middle">
          {total}
        </text>
        <text className="country-donut__caption" x="50%" y="62%" textAnchor="middle" dominantBaseline="middle">
          {t('joined')}
        </text>
      </svg>

      <ul className="country-donut__legend">
        {slices.map((s) => (
          <li key={s.name}>
            <button
              type="button"
              className={`country-donut__key${s.name === selected ? ' is-on' : ''}`}
              onMouseEnter={() => onSelect(s.name)}
              onFocus={() => onSelect(s.name)}
              onClick={() => onSelect(s.name)}
            >
              <span className="country-donut__swatch" style={{ background: s.color }} />
              <span className="country-donut__name">{s.label}</span>
              <span className="country-donut__count">{s.joined}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
